import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { AUTH_API_BASE_URL } from "../config/api-config";
import {
  authConfig, getBrowserBinding, rememberPending, forgetPending, currentReturnPath, claimResume,
} from "../utils/bankIdSession";
import {
  MODE_SAME_DEVICE, MODE_OTHER_DEVICE, HINT_STORAGE_UNAVAILABLE, HINT_INCOMPATIBLE_SERVER,
} from "./useBankId";

const BASE = `${AUTH_API_BASE_URL}/BankId`;

const unwrap = (res) => res?.data?.result ?? res?.data;
const hintOf = (err) => err?.response?.data?.hintCode ?? err?.response?.data?.error ?? null;

// The signing endpoints belong to the signed-in user (the order is bound to their contract and
// role), so every call carries the same bearer token the rest of the app sends, plus this
// browser's binding key.
export function useBankIdSign() {
  const [status, setStatus] = useState(null);
  const [hintCode, setHintCode] = useState(null);
  const [personalNumber, setPersonalNumber] = useState(null);
  const [error, setError] = useState(null);
  const [orderRef, setOrderRef] = useState(null);
  const [autoStartToken, setAutoStartToken] = useState(null);
  const [qrStartToken, setQrStartToken] = useState(null);
  const [qrStartSecret, setQrStartSecret] = useState(null);

  const pollTimer = useRef(null);
  const runRef = useRef(0);
  const activeRef = useRef(null);
  const startingRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
  }, []);

  // Unmounting (dialog closed, page left) stops this component's polling and its "page visible"
  // listeners. The BankID order itself is not cancelled: a pending order can still be resumed.
  const listenersRef = useRef(null);
  useEffect(() => () => {
    runRef.current += 1;
    stopPolling();
    listenersRef.current?.();
  }, [stopPolling]);

  const resetOrder = () => {
    setOrderRef(null); setAutoStartToken(null); setQrStartToken(null); setQrStartSecret(null);
  };

  const finish = (ref) => {
    if (activeRef.current === ref) activeRef.current = null;
    forgetPending(ref);
  };

  const poll = useCallback((ref, run, opts) => {
    // A BankID order lives ~3 minutes; keep polling through transient errors (phone switching
    // to the BankID app, flaky mobile network, API restart) until then.
    const deadline = Date.now() + 3.5 * 60 * 1000;
    let transientFailures = 0;
    const onVisible = () => {
      if (document.visibilityState !== "visible" || runRef.current !== run) return;
      clearTimeout(pollTimer.current);
      tick();
    };
    const stopListening = () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    listenersRef.current?.();          // only one live poll loop's listeners at a time
    listenersRef.current = stopListening;
    const tick = async () => {
      if (runRef.current !== run) { stopListening(); return; }
      try {
        const collected = unwrap(await axios.post(`${BASE}/CollectSign`,
          { orderRef: ref, browserBinding: getBrowserBinding() }, authConfig()));
        if (runRef.current !== run) { stopListening(); return; }
        transientFailures = 0;

        setStatus(collected.status);
        setHintCode(collected.hintCode ?? null);

        if (collected.status === "complete") {
          finish(ref);
          setPersonalNumber(collected.personalNumber);
          // The caller still submits the orderRef (DealCompletedBy*), which is idempotent with the
          // server's own recording; signatureRecorded says whether that already happened.
          stopListening();
          opts.onComplete?.(collected.personalNumber, collected.name, ref, {
            signatureRecorded: collected.signatureRecorded === true,
          });
          return;
        }
        if (collected.status === "failed") { stopListening(); finish(ref); opts.onFailed?.(collected.hintCode); return; }
        pollTimer.current = setTimeout(tick, 2000);
      } catch (err) {
        if (runRef.current !== run) { stopListening(); return; }
        const httpStatus = err.response?.status;
        const transient = !err.response || httpStatus >= 500 || httpStatus === 429;
        if (transient && Date.now() < deadline) {
          transientFailures += 1;
          pollTimer.current = setTimeout(tick, Math.min(8000, 2000 * 2 ** Math.min(transientFailures - 1, 2)));
          return;
        }
        stopListening();
        finish(ref);
        const hint = hintOf(err) ?? "expiredTransaction";
        setError(err.response?.data?.message ?? err.message ?? "BankID signing error");
        setStatus("failed");
        setHintCode(hint);
        opts.onFailed?.(hint);
      }
    };
    tick();
  }, []);

  const cancel = useCallback(async (ref) => {
    runRef.current += 1;
    stopPolling();
    startingRef.current = false;
    const activeOrder = ref ?? activeRef.current;
    activeRef.current = null;
    resetOrder();
    setStatus(null); setPersonalNumber(null);
    if (activeOrder) {
      forgetPending(activeOrder);
      try {
        await axios.post(`${BASE}/Cancel`, { orderRef: activeOrder, browserBinding: getBrowserBinding() }, authConfig());
      } catch (_) { /* best-effort */ }
    }
  }, [stopPolling]);

  const start = useCallback(async (opts = {}) => {
    if (startingRef.current) return;          // a double click must not create a second order
    runRef.current += 1;
    const run = runRef.current;
    stopPolling();
    setError(null); setStatus(null); setHintCode(null); setPersonalNumber(null);
    // Drop the previous order's tokens so nothing can launch the BankID app with a stale one.
    resetOrder();

    const fail = (hint, message) => {
      setError(message ?? hint); setStatus("failed"); setHintCode(hint);
      opts.onFailed?.(hint);
    };

    const browserBinding = getBrowserBinding();
    if (!browserBinding) { fail(HINT_STORAGE_UNAVAILABLE); return; }

    const requestedMode = opts.mode === MODE_SAME_DEVICE ? MODE_SAME_DEVICE : MODE_OTHER_DEVICE;
    startingRef.current = true;
    let authData;
    try {
      // Real BankID *signing* order. The text and hidden data are generated server-side from
      // the stored contract; contractId + role bind the order (ownership-checked).
      authData = unwrap(await axios.post(`${BASE}/Sign`, {
        mode: requestedMode,
        browserBinding,
        returnPath: requestedMode === MODE_SAME_DEVICE ? currentReturnPath() : null,
        contractId: opts.contractId ?? null,
        role: opts.role ?? null,
        // Language of the text BankID shows; the text itself is generated server-side.
        lang: opts.lang ?? null,
        userVisibleData: opts.userVisibleData ?? null,
      }, authConfig()));
    } catch (err) {
      startingRef.current = false;
      if (runRef.current !== run) return;
      fail(hintOf(err) ?? "startFailed", err.response?.data?.message ?? err.message ?? "BankID auth error");
      return;
    }
    startingRef.current = false;

    if (runRef.current !== run) {
      axios.post(`${BASE}/Cancel`, { orderRef: authData?.orderRef, browserBinding }, authConfig()).catch(() => {});
      return;
    }

    if (authData?.binding !== "browser" || authData?.mode !== requestedMode) {
      axios.post(`${BASE}/Cancel`, { orderRef: authData?.orderRef }, authConfig()).catch(() => {});
      fail(HINT_INCOMPATIBLE_SERVER);
      return;
    }

    activeRef.current = authData.orderRef;
    if (requestedMode === MODE_SAME_DEVICE) {
      // Lets the contract page resume this signature after a reload or the app's return in a new tab.
      rememberPending({ orderRef: authData.orderRef, purpose: "sign", contractId: opts.contractId, role: opts.role });
      // This page polls it already: a same-tab return must not resume it a second time.
      claimResume(authData.orderRef);
    }
    setOrderRef(authData.orderRef);
    setAutoStartToken(authData.autoStartToken ?? null);
    setQrStartToken(authData.qrStartToken ?? null);
    setQrStartSecret(authData.qrStartSecret ?? null);
    setStatus("pending");

    poll(authData.orderRef, run, opts);
  }, [poll, stopPolling]);

  /** Resume an existing signing order. Never launches the BankID app or starts a new order. */
  const resume = useCallback((savedOrderRef, opts = {}) => {
    runRef.current += 1;
    const run = runRef.current;
    stopPolling();
    resetOrder();
    setError(null); setHintCode(null); setStatus("pending");
    setOrderRef(savedOrderRef);
    activeRef.current = savedOrderRef;
    poll(savedOrderRef, run, opts);
  }, [poll, stopPolling]);

  return { start, cancel, resume, status, hintCode, personalNumber, orderRef, autoStartToken, qrStartToken, qrStartSecret, error };
}
